/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Button, SkeletonText } from 'carbon-addons-iot-react';
import { Column16 } from '@carbon/icons-react';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { v4 as uuidv4 } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
import { useIntl, defineMessages } from 'react-intl';
import hash from 'object-hash';

import CommonTable from '../CommonTable/CommonTable';

import TableViewDropdown from './TableViewDropdown/TableViewDropdown';
import ManageViewsModal from './ManageViewsModal/ManageViewsModal';
import CustomizeColumnsModal from './CustomizeColumnsModal/CustomizeColumnsModal';
import SaveViewModal from './SaveViewModal/SaveViewModal';

import { useTableViews } from 'customHooks';
import { TableViewType, PALColumnType, PALTableType, SelectableViewColumnType } from 'TableTypes';
import { mapToPALFilters } from 'tableUtils';

const messages = defineMessages({
  customizeColumns: 'Customize columns',
  customColumnsText: 'Custom columns',
});

export interface CommonTableWithViewsType extends PALTableType {
  userId: string;
  tableId: string;
  /** Is the stateful draft view of changes that are in-progress to a view, this contains only the visible columns */
  selectedView: TableViewType;
  setSelectedView: React.Dispatch<React.SetStateAction<TableViewType>>;
  /** if no view is selected, we should use this as the default view */
  defaultView: TableViewType;
  /** Specify the properties of each PAL columns that will render  */
  columns: PALColumnType[];
  customColumnsText: string;
  i18n: Record<string, string>;
  /** full list of available columns for selection in the view, the ordering and visibility, and drilldown decisions are captured inside the selectedView */
  availableColumns: SelectableViewColumnType[];
  /** Should the common table with views allow customized columns */
  shouldShowCustomizeColumns: boolean;
}

/**
 * Provides view management to underlying CommonTable by:
 * keeping track of which view to show
 * holding view state if anything has been changed and whether a view needs to be saved
 * creating handlers for all view management
 * rendering the view modals
 * updating the view to include the view dropdown
 */
const CommonTableWithViews = ({
  view: viewProp,
  actions: actionsProp,
  options: optionsProp,
  tableId,
  userId,
  selectedView,
  setSelectedView,
  defaultView,
  availableColumns,
  customColumnsText: customColumnsTextProp,
  shouldShowCustomizeColumns = true,
  columns,
  ...props
}: CommonTableWithViewsType): JSX.Element => {
  const { formatMessage } = useIntl();

  const customColumnsText = customColumnsTextProp || formatMessage(messages.customColumnsText);

  const {
    tableViews = [],
    updateTableViews,
    isLoading: isLoadingTableViews,
  } = useTableViews(tableId, 'tableViews', userId);

  // save view modal will be open if viewToSave is set
  const [viewToSave, setViewToSave] = useState<TableViewType | null>(null);

  const [isCustomizeColumnsModalOpen, setIsCustomizeColumnsModalOpen] = useState(false);
  const [isManageViewsModalOpen, setIsManageViewsModalOpen] = useState(false);

  // The currentUserViewRef is the original state of the selected view
  // useRef is preferred over useState so that the value can be updated without causing a
  // rerender of the table.
  const currentUserViewRef = useRef(selectedView);

  // If our tableviews have finished loading and we haven't found a default one, then use our defaultView property
  useEffect(() => {
    if (!selectedView && !isLoadingTableViews) {
      // Is there a table view marked as the default one?, if not use the tables default view
      const tableViewMarkedDefault = tableViews?.find(view => view.isDefault);
      const viewToUseAsDefault = cloneDeep(tableViewMarkedDefault || defaultView);
      // need to wait for the table views to see if we might get one
      setSelectedView(viewToUseAsDefault);
      currentUserViewRef.current = viewToUseAsDefault;
    }
  }, [setSelectedView, defaultView, tableViews, selectedView, isLoadingTableViews]);

  // keep track of where the current view has been edited
  const isSelectedViewEdited = useMemo(
    () => !isEqual(selectedView, currentUserViewRef.current),
    [selectedView]
  );

  const tableViewDropdown = useMemo(
    () => (
      <TableViewDropdown
        key="table-view-dropdown"
        selectedViewId={selectedView?.id}
        isSelectedViewEdited={isSelectedViewEdited}
        views={tableViews
          ?.filter(view => view.isPublic || view.userId === userId)
          .map(({ id, title }) => ({ id, text: title }))}
        onSaveAsNewView={(): void => {
          // Remove the id so the save modal will know this is a new view
          setViewToSave({ ...selectedView, id: undefined, userId });
        }}
        onManageViews={(): void => {
          setIsManageViewsModalOpen(true);
        }}
        onChangeView={({ id }): void => {
          // if the user selects 'View all', reset the view to the defaults
          const selected = tableViews?.find(view => view.id === id);
          setSelectedView(selected || defaultView);
          currentUserViewRef.current = selected || defaultView;
        }}
        onSaveChanges={(): void => {
          setViewToSave({ ...selectedView, userId });
        }}
      />
    ),
    [isSelectedViewEdited, selectedView, defaultView, setSelectedView, tableViews, userId]
  );

  const customizeColumnsButton = useMemo(
    () =>
      shouldShowCustomizeColumns ? (
        <Button
          key="customize-columns-button"
          title={formatMessage(messages.customizeColumns)}
          kind="ghost"
          renderIcon={Column16}
          hasIconOnly
          onClick={(): void => setIsCustomizeColumnsModalOpen(true)}
          tooltipPosition="left"
          iconDescription={formatMessage(messages.customizeColumns)}
        />
      ) : undefined,
    [formatMessage, shouldShowCustomizeColumns]
  );

  const view = useMemo(
    () => ({
      ...viewProp,
      table: {
        ...omit(viewProp.table, 'ordering'),
        sort: viewProp.table?.hasOwnProperty('sort') // on initial view, no sort property is set so use selectedView sort
          ? viewProp.table.sort // once you click sort on column to undefine a sort use it
          : selectedView?.table?.sort,
        loadingState: {
          isLoading: viewProp.table?.loadingState?.isLoading,
        },
      },
      // TODO: match their format for filters
      filters: mapToPALFilters(selectedView?.filters),
      toolbar: {
        ...viewProp?.toolbar,
        // the activeBar must be set to 'filter' for the search or filters to be applied to the underlyng StatefulTable
        activeBar: !isEmpty(selectedView?.filters) ? 'filter' : null,
        search: { defaultValue: selectedView?.searchValue },
        // need to add the TableViewDropdown into the customToolbarContent
        customToolbarContent: (
          <>
            {tableViewDropdown}
            {customizeColumnsButton}
            {viewProp.toolbar?.customToolbarContent ? viewProp.toolbar.customToolbarContent : null}
          </>
        ),
      },
    }),
    [customizeColumnsButton, tableViewDropdown, viewProp, selectedView]
  );

  const onUserViewModified = useCallback(
    newState => {
      const {
        view: {
          filters,
          table: { sort },
        },
        // The default search value is not updated just because the user modifies
        // the actual search input so in order to set the defaultValue we can access
        // the internal "currentSearchValue" via a special state prop
        state: { currentSearchValue },
      } = newState;

      if (selectedView) {
        // check if search changed
        if (
          selectedView.searchValue !== currentSearchValue &&
          currentUserViewRef.current.searchValue !== currentSearchValue
        ) {
          setSelectedView(
            (state: TableViewType): TableViewType => ({
              ...state,
              searchValue: currentSearchValue,
            })
          );
        }

        // check if filters changed
        let didFiltersChange = false;
        const clonedFilters = { ...selectedView.filters };
        // TODO: match their format for filters
        filters.forEach(({ columnId, value }) => {
          if (
            (!selectedView.filters[columnId] || !isEqual(selectedView.filters[columnId], value)) &&
            (!currentUserViewRef.current.filters[columnId] ||
              !isEqual(currentUserViewRef.current.filters[columnId], value))
          ) {
            clonedFilters[columnId] = value;
            didFiltersChange = true;
          }
        });

        if (didFiltersChange) {
          setSelectedView(
            (state: TableViewType): TableViewType => ({ ...state, filters: clonedFilters })
          );
        }
        // Check if sort direction changed and update selected view
        if (!isEqual(sort, selectedView?.table?.sort)) {
          setSelectedView(
            (state: TableViewType): TableViewType => ({
              ...state,
              table: { ...state.table, sort },
            })
          );
        }
      }
    },
    [selectedView, setSelectedView]
  );

  const actions = useMemo(
    () => ({
      ...actionsProp,
      onUserViewModified,
      toolbar: {
        ...actionsProp?.toolbar,
        onClearAllFilters: (): void => {
          setSelectedView((state: TableViewType) => ({ ...state, filters: {} }));
        },
      },
    }),
    [actionsProp, onUserViewModified, setSelectedView]
  );

  const options = useMemo(
    () => ({
      ...optionsProp,
      hasUserViewManagement: true,
    }),
    [optionsProp]
  );

  // I have order the default columns according to the selectedView.columns, update to use the latest isVisible setting,
  const PALColumns = useMemo(
    () =>
      selectedView?.columns
        .map(selectedViewColumn => {
          const matchingColumn = columns.find(PALColumn => PALColumn.id === selectedViewColumn.id);
          if (!matchingColumn) {
            console.error('Error because the matched column does not exist');
            return null;
          }
          return matchingColumn;
        })
        .filter(i => i), // filter out any missing dimensions
    [columns, selectedView?.columns]
  );

  return !isLoadingTableViews && !isEmpty(selectedView) ? (
    <>
      <CommonTable
        {...props} // eslint-disable-line react/jsx-props-no-spreading
        columns={PALColumns}
        key={`common-table-with-views-${hash({
          columns: PALColumns,
          viewId: selectedView.id,
        })}`} // Need to regen the table in real time if changing it in the editor
        view={view}
        actions={actions}
        options={options}
      />
      {viewToSave ? (
        <SaveViewModal
          onSave={(): void => {
            const viewToSaveIndex = tableViews.findIndex(
              tableView => tableView.id === viewToSave.id
            );

            const clonedViews = [...tableViews];
            // if isDefault is swapped to true, swap all other views to isDefault = false
            if (viewToSave.isDefault) {
              clonedViews.map(clonedView => ({ ...clonedView, isDefault: false }));
            }

            // if the view doesn't exist, concat
            if (viewToSaveIndex === -1) {
              const updatedView = { ...viewToSave, id: uuidv4() };
              updateTableViews([...tableViews, updatedView]);
              setSelectedView(updatedView);
              currentUserViewRef.current = updatedView;
            } // otherwise update the view
            else {
              clonedViews[viewToSaveIndex] = viewToSave;
              updateTableViews(clonedViews);
              setSelectedView(viewToSave);
              currentUserViewRef.current = viewToSave;
            }

            setViewToSave(undefined);
          }}
          onClose={(): void => {
            setViewToSave(undefined);
          }}
          onChange={(input: Record<string, never>): void => {
            setViewToSave(state => ({ ...state, ...input }));
          }}
          isSendingData={false}
          error={undefined}
          view={viewToSave}
        />
      ) : null}
      {isManageViewsModalOpen ? (
        <ManageViewsModal
          // if editing from the manage view modal, close this modal, select the view, and open the save view modal
          onEdit={(viewId): void => {
            setIsManageViewsModalOpen(false);
            const viewToEdit = tableViews.find(tableView => tableView.id === viewId);
            setSelectedView(viewToEdit);
            setViewToSave(viewToEdit);
          }}
          onDelete={(viewId): void => {
            const viewToDeleteIndex = tableViews.findIndex(tableView => tableView.id === viewId);
            const clonedViews = cloneDeep(tableViews);
            clonedViews.splice(viewToDeleteIndex, viewToDeleteIndex + 1);
            // update the table view database
            updateTableViews(clonedViews);
            // unselect the view if was selected
            if (selectedView.id === viewId) {
              setSelectedView(undefined);
              currentUserViewRef.current = undefined;
            }
          }}
          onClose={(): void => setIsManageViewsModalOpen(false)}
          // defaultViewId={defaultViewId}
          error={undefined}
          isLoading={false}
          currentUser={userId}
          tableViews={tableViews}
        />
      ) : null}
      {isCustomizeColumnsModalOpen ? (
        <CustomizeColumnsModal
          onClose={(): void => setIsCustomizeColumnsModalOpen(false)}
          columns={selectedView?.columns}
          availableColumns={availableColumns}
          onSubmit={(updatedColumns): void => {
            const clonedSelectedView = {
              ...selectedView,
              columns: updatedColumns,
            };

            // only put the view in draft state for now do not save it
            setSelectedView(clonedSelectedView);
            setIsCustomizeColumnsModalOpen(false);
          }}
          customColumnsText={customColumnsText}
        />
      ) : null}
    </>
  ) : (
    // We have to render a custom skeleton text here because we don't know which columns will be visible:
    // https://github.com/carbon-design-system/carbon-addons-iot-react/issues/2436
    <div className="common-table-skeleton-text">
      <SkeletonText lineCount={3} paragraph />
    </div>
  );
};

export default CommonTableWithViews;
